import type { AxiosError } from "axios";

import type { StripeGateClosedPayload } from "~/types/stripe-gate";
import { isCapabilityDenial } from "~/lib/utils";
import { SERVER_FAULT_MESSAGE, plainServerFaultMessage } from "~/lib/server-fault-message";

/**
 * Check if an error is a network error (no response from server)
 */
export function isNetworkError(error: any): boolean {
  // Axios network errors have no response object and specific error codes
  const code = error?.code || error?.errorCode;
  if (code === "ERR_NETWORK") return true;
  if (code === "ECONNABORTED") return true; // Timeout
  if (error?.message === "Network Error") return true;
  if (!error?.response && error?.request) return true; // Request sent but no response

  // Check wrapped errors (e.g. from handleApiError which attaches originalError)
  if (error?.originalError) return isNetworkError(error.originalError);

  return false;
}

/**
 * Strip internal backend error details (Prisma exceptions, stack traces,
 * class names) from a message string and replace with a generic friendly
 * message. This prevents raw database / ORM error text from being shown to
 * restaurant managers.
 */
function sanitizeBackendMessage(message: string): string {
  const internalPatterns = [
    "PrismaClientKnownRequestError",
    "PrismaClientUnknownRequestError",
    "PrismaClientValidationError",
    "PrismaClientRustPanicError",
    "PrismaClientInitializationError",
    "Unique constraint failed",
    "Foreign key constraint failed",
    "An operation failed because it depends on",
    "Error occurred in the query engine",
  ];
  const isInternal = internalPatterns.some((p) => message.includes(p));
  if (isInternal) {
    return SERVER_FAULT_MESSAGE;
  }
  return message;
}

/**
 * Get a user-friendly error message from any error type
 * Handles network errors, API errors, and generic errors.
 *
 * A 5xx that carries only stock text ("Internal server error", "Request failed
 * with status code 502") reads as a fault on our side, with who can fix it
 * (A-55 / A-61) — see ~/lib/server-fault-message.
 */
export function getErrorMessage(error: any): string {
  return plainServerFaultMessage(resolveErrorMessage(error), getHttpStatus(error));
}

function resolveErrorMessage(error: any): string {
  // 1. Check for timeout errors specifically
  if (isTimeoutError(error)) {
    return "The request timed out. Please check your internet connection or try again later.";
  }

  // 2. Check for other network/connection errors
  if (isNetworkError(error)) {
    // If we can detect that the user is actually offline
    if (
      typeof window !== "undefined" &&
      window.navigator &&
      window.navigator.onLine === false
    ) {
      return "You appear to be offline. Please check your internet connection and try again.";
    }

    // If online but network error, it's likely a server issue, firewall, or CORS
    return "Connection failed. The server might be unreachable.";
  }

  // 3. API error responses
  const apiError =
    error?.response?.data || error?.originalError?.response?.data;
  if (apiError) {
    // Structured shift-conflict responses carry per-issue messages under
    // details.conflicts[].issues[].message — surface those instead of the
    // generic top-level "Conflicts detected for one or more employees".
    const conflictMessage = extractStructuredConflictMessage(apiError);
    if (conflictMessage) return conflictMessage;

    // Bug-208: NestJS's ValidationPipe returns `message` as an ARRAY of
    // per-field messages:
    //
    //   { error: "Bad Request",
    //     message: ["heroImageUrl must be a URL address", ...] }
    //
    // Only the string case was handled, so an array fell through to
    // `apiError.error` below and EVERY validation failure in the app surfaced
    // as the toast "Bad Request". That is why "unable to save storefront
    // settings" was unreproducible — the API was correctly naming the field it
    // rejected and the client was discarding it.
    //
    // All of them are joined, not just the first: fixing one field only to be
    // rejected for the next is a miserable loop.
    if (Array.isArray(apiError.message)) {
      const fieldMessages = apiError.message
        .filter((entry: unknown): entry is string => typeof entry === "string")
        .map(sanitizeBackendMessage);
      if (fieldMessages.length > 0) return fieldMessages.join(". ");
    }

    // Try different response formats — sanitize any backend internal error
    // messages (Prisma exceptions, stack traces) before surfacing to the user.
    if (typeof apiError.message === "string") {
      return sanitizeBackendMessage(apiError.message);
    }
    if (typeof apiError.error === "string") {
      return sanitizeBackendMessage(apiError.error);
    }
    if (typeof apiError.detail === "string") {
      return sanitizeBackendMessage(apiError.detail);
    }
    if (
      Array.isArray(apiError.non_field_errors) &&
      apiError.non_field_errors[0]
    ) {
      return apiError.non_field_errors[0];
    }
  }

  // 4. Axios error message
  if (error?.message && typeof error.message === "string") {
    // Map generic Axios messages to something friendlier
    if (error.message === "Network Error" || error.code === "ERR_NETWORK") {
      return "Unable to connect to the server. Please check if the server is running or contact support.";
    }
    return error.message;
  }

  // 5. Fallback
  return "An unexpected error occurred. Please try again.";
}

interface ConflictIssue {
  type?: string;
  message?: string;
  severity?: string;
}

interface ConflictEntry {
  employeeName?: string;
  issues?: ConflictIssue[];
}

/**
 * Extract a human-readable message from the structured shift-conflict
 * response shape:
 *   { details: { conflicts: [{ employeeName, issues: [{ message, ... }] }] } }
 * Returns undefined when the payload doesn't match this shape.
 */
export function extractStructuredConflictMessage(
  apiError: any,
): string | undefined {
  const conflicts = apiError?.details?.conflicts;
  if (!Array.isArray(conflicts) || conflicts.length === 0) return undefined;

  const messages: string[] = [];
  for (const conflict of conflicts as ConflictEntry[]) {
    const issues = conflict?.issues;
    if (!Array.isArray(issues)) continue;
    for (const issue of issues) {
      if (typeof issue?.message !== "string") continue;
      const name = conflict.employeeName;
      messages.push(name ? `${name}: ${issue.message}` : issue.message);
    }
  }

  if (messages.length === 0) return undefined;
  return messages.join(" • ");
}

/**
 * Check if error is a timeout error
 */
export function isTimeoutError(error: any): boolean {
  const code = error?.code || error?.errorCode;
  if (code === "ECONNABORTED" || code === "ETIMEDOUT") return true;

  // Check wrapped errors (e.g. from handleApiError which attaches originalError)
  if (error?.originalError) return isTimeoutError(error.originalError);

  return false;
}

/**
 * Extract HTTP status code from any error shape.
 *
 * Handles three common shapes in this codebase:
 *  1. Raw AxiosError          → error.response.status
 *  2. Wrapped by handleApiError → error.statusCode  (+ error.originalError)
 *  3. Deeply nested           → recursive via originalError
 *
 * Returns `undefined` when no status can be determined.
 */
export function getHttpStatus(error: any): number | undefined {
  // handleApiError attaches statusCode directly
  if (typeof error?.statusCode === "number") return error.statusCode;

  // Raw axios error
  if (typeof error?.response?.status === "number") return error.response.status;

  // Walk wrapped errors
  if (error?.originalError) return getHttpStatus(error.originalError);

  return undefined;
}

/**
 * Check if error is a server error (5xx)
 */
export function isServerError(error: any): boolean {
  const status = getHttpStatus(error);
  return status !== undefined && status >= 500 && status < 600;
}

/**
 * Check if error is a permission/authorization error (403)
 */
export function isPermissionError(error: any): boolean {
  return getHttpStatus(error) === 403;
}

/**
 * Is this the viewer's ACCESS LEVEL refusing them — "not for you" — rather than
 * the plan or the account?
 *
 * The backend answers 403 for three different things, and only the first is a
 * permission refusal: a missing permission key; a plan that does not include the
 * feature (`FEATURE_NOT_AVAILABLE`, which the global paywall portal answers); and a
 * subscription lockout (`error: "SubscriptionLocked"`). Telling an Owner whose
 * account is locked to "ask your organization owner" is wrong, and hides the API's
 * own reason. The named capability denials (`isCapabilityDenial`, ~/lib/utils) are
 * the same list the error reporter already downgrades, so the two cannot disagree.
 *
 * Reads the fields `handleApiError` attaches (`errorCode`, `errorData`) and, for a
 * raw axios error, the response body.
 */
export function isAccessDeniedError(error: any): boolean {
  if (!isPermissionError(error)) return false;
  if (error?.featureGate) return false;
  const body = error?.errorData ?? error?.response?.data ?? error?.originalError?.response?.data;
  const code: string | undefined =
    error?.errorCode ?? body?.details?.code ?? body?.code ?? error?.originalError?.errorCode;
  return !isCapabilityDenial(403, code, body);
}

/**
 * Check if error is a duplicate/conflict error (409)
 */
export function isDuplicateError(error: any): boolean {
  return getHttpStatus(error) === 409;
}

/**
 * Check if error is a rate limit error (429)
 */
export function isRateLimitError(error: any): boolean {
  return getHttpStatus(error) === 429;
}

/**
 * XD-006 — Detect the 422 STRIPE_GATE_CLOSED response shape attached by
 * the axiosPrivate interceptor. Returns the parsed payload or undefined.
 */
export function getStripeGateClosed(
  error: any,
): StripeGateClosedPayload | undefined {
  if (error?.stripeGate?.code === "STRIPE_GATE_CLOSED") {
    return error.stripeGate as StripeGateClosedPayload;
  }
  if (error?.originalError) return getStripeGateClosed(error.originalError);
  return undefined;
}

export function isStripeGateClosedError(error: any): boolean {
  return Boolean(getStripeGateClosed(error));
}
